/**
 * Action Embeddings — TF-IDF semantic scoring for action retrieval.
 *
 * Replaces keyword substring matching (v0) with TF-IDF vector similarity
 * (v1). Pre-computes document vectors for all registered actions on first
 * use, then scores user queries via cosine similarity.
 *
 * Key improvements over v0 keyword scoring:
 * - **TF-IDF weighting**: frequent terms in a single action matter less
 *   if they appear across many actions (IDF dampening)
 * - **Domain synonyms**: "binding" matches "dock", "visualize" matches
 *   "display", "small molecule" matches "compound"
 * - **Simple stemming**: "compounds" → "compound", "docking" → "dock"
 * - **Cosine similarity**: proper geometric comparison of vectors
 *
 * Runs entirely client-side — no backend embedding model required.
 *
 * @module gibbs/text-to-action/actionEmbeddings
 */

// ─── Domain synonym map ──────────────────────────────────────────────────────
// Maps common user terms to action-vocabulary equivalents. When a user says
// "binding", we also inject "dock" and "score" into their query vector.

const SYNONYMS = {
    binding: ['dock', 'score', 'affinity', 'interaction'],
    bind: ['dock', 'score', 'affinity'],
    affinity: ['dock', 'score', 'gnina', 'energy'],
    visualize: ['display', 'show', 'view', 'render'],
    visualization: ['display', 'show', 'view'],
    see: ['show', 'display', 'view'],
    look: ['show', 'display', 'view'],
    molecule: ['compound', 'ligand'],
    molecules: ['compound', 'ligand'],
    drug: ['compound', 'ligand'],
    drugs: ['compound', 'ligand'],
    ligand: ['compound'],
    protein: ['receptor', 'target', 'structure'],
    receptor: ['protein', 'target'],
    target: ['protein', 'receptor'],
    structure: ['protein', 'fold'],
    pose: ['dock', 'conformation'],
    poses: ['dock', 'conformation'],
    minimize: ['energy', 'optimize', 'relax'],
    optimize: ['minimize', 'energy'],
    relax: ['minimize', 'energy'],
    rank: ['score', 'sort', 'compare'],
    ranking: ['score', 'sort', 'compare'],
    best: ['score', 'rank', 'top'],
    analyze: ['show', 'interaction', 'score', 'energy'],
    analysis: ['show', 'interaction', 'score', 'energy'],
    hide: ['remove', 'toggle', 'off'],
    remove: ['delete', 'hide', 'clear'],
    delete: ['remove', 'clear', 'zap'],
    clear: ['remove', 'delete', 'zap'],
    color: ['scheme', 'display', 'render'],
    cartoon: ['display', 'style', 'render'],
    sticks: ['display', 'style', 'render'],
    surface: ['display', 'render', 'solvent'],
    ribbon: ['cartoon', 'display'],
    hbond: ['hydrogen', 'bond', 'interaction'],
    hydrogen: ['hbond', 'polar'],
    solvation: ['solvent', 'water', 'desolvation'],
    water: ['solvent', 'solvation'],
    hotspot: ['fragment', 'map', 'boltzmann'],
    fragment: ['grow', 'hotspot', 'fbdd'],
    grow: ['fragment', 'extend', 'elaborate'],
    screen: ['library', 'virtual', 'batch'],
    library: ['screen', 'virtual', 'batch', 'collection'],
    virtual: ['screen', 'library'],
    generate: ['novo', 'reinvent', 'create'],
    novo: ['generate', 'reinvent', 'design'],
    reinvent: ['generate', 'novo', 'design'],
    design: ['generate', 'novo', 'reinvent'],
    generative: ['generate', 'novo', 'reinvent'],
    sketch: ['draw', 'editor', '2d'],
    draw: ['sketch', 'editor'],
    import: ['load', 'file', 'upload'],
    export: ['save', 'download', 'file'],
    undo: ['revert', 'back'],
    redo: ['forward'],
    reset: ['restore', 'default', 'clear'],
    fold: ['predict', 'structure', 'alphafold', 'boltz'],
    predict: ['fold', 'score', 'admet'],
    admet: ['predict', 'maplight', 'toxicity', 'absorption'],
    zoom: ['view', 'camera'],
    camera: ['view', 'zoom'],
};

// ─── Simple stemmer ──────────────────────────────────────────────────────────
// Aggressive suffix stripping for English morphology. Not a full Porter
// stemmer — just handles the most common drug-discovery / UI terms.

const SUFFIX_RULES = [
    [/ization$/i, ''],
    [/ations$/i, ''],
    [/ation$/i, ''],
    [/ising$/i, ''],
    [/izing$/i, ''],
    [/ities$/i, ''],
    [/ments$/i, ''],
    [/ness$/i, ''],
    [/ings$/i, ''],
    [/ment$/i, ''],
    [/tion$/i, ''],
    [/sion$/i, ''],
    [/able$/i, ''],
    [/ible$/i, ''],
    [/ence$/i, ''],
    [/ance$/i, ''],
    [/ous$/i, ''],
    [/ing$/i, ''],
    [/ies$/i, 'y'],
    [/ers$/i, ''],
    [/ed$/i, ''],
    [/er$/i, ''],
    [/es$/i, ''],
    [/ly$/i, ''],
    [/s$/i, ''],
];

/**
 * Stem a word by stripping common suffixes.
 * @param {string} word
 * @returns {string}
 */
function stem(word) {
    if (word.length <= 3) return word;
    for (const [pattern, replacement] of SUFFIX_RULES) {
        if (pattern.test(word)) {
            const result = word.replace(pattern, replacement);
            // Don't reduce below 3 chars
            if (result.length >= 3) return result;
        }
    }
    return word;
}

// ─── Stop words ──────────────────────────────────────────────────────────────

const STOP_WORDS = new Set([
    'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been',
    'to', 'for', 'in', 'on', 'at', 'by', 'with', 'from', 'of',
    'and', 'or', 'but', 'not', 'no', 'it', 'its',
    'this', 'that', 'these', 'those',
    'my', 'me', 'i', 'you', 'your', 'we', 'our',
    'can', 'will', 'do', 'does', 'did', 'has', 'have', 'had',
    'please', 'want', 'would', 'like', 'could', 'should',
    'just', 'also', 'very', 'then', 'so', 'if',
    'use', 'using', 'used',
]);

// ─── Tokenizer ───────────────────────────────────────────────────────────────

/**
 * Tokenize text into stemmed terms, stripping stop words.
 * @param {string} text
 * @returns {string[]}
 */
function tokenize(text) {
    if (!text) return [];
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .split(/[\s-]+/)
        .filter((w) => w.length > 1 && !STOP_WORDS.has(w))
        .map(stem);
}

/**
 * Expand tokens with domain synonyms. Each synonym is added with
 * reduced weight (handled at vector construction time).
 * @param {string[]} tokens
 * @returns {{term: string, weight: number}[]}
 */
function expandWithSynonyms(tokens) {
    const expanded = [];
    for (const t of tokens) {
        expanded.push({ term: t, weight: 1.0 });
        const syns = SYNONYMS[t];
        if (syns) {
            for (const s of syns) {
                expanded.push({ term: stem(s), weight: 0.5 });
            }
        }
    }
    return expanded;
}

// ─── TF-IDF Engine ──────────────────────────────────────────────────────────

/**
 * Lazy-initialized corpus state.
 * @type {{
 *   vocabulary: Map<string, number>,
 *   idf: Float64Array,
 *   actionVectors: Map<string, Float64Array>,
 *   initialized: boolean
 * }}
 */
const corpus = {
    vocabulary: new Map(),
    idf: null,
    actionVectors: new Map(),
    initialized: false,
};

/**
 * Build action text corpus for TF-IDF.
 * Concatenates id (split on '-'), name, description, category.
 *
 * @param {Object} action
 * @returns {string}
 */
function actionToText(action) {
    const parts = [
        (action.id || '').replace(/-/g, ' '),
        action.name || '',
        action.description || '',
        action.category || '',
    ];
    // Include param names and descriptions for richer matching
    for (const p of (action.inputs || [])) {
        parts.push(p.name || '');
        parts.push(p.description || '');
    }
    for (const p of (action.parameters || [])) {
        parts.push(p.name || '');
        parts.push(p.description || '');
    }
    return parts.join(' ');
}

/**
 * Build vocabulary and IDF from a list of tokenized documents.
 * @param {string[][]} documents - Array of tokenized documents
 */
function buildVocabulary(documents) {
    const termSet = new Set();
    for (const doc of documents) {
        for (const term of doc) termSet.add(term);
    }

    const vocab = new Map();
    let idx = 0;
    for (const term of termSet) {
        vocab.set(term, idx++);
    }
    corpus.vocabulary = vocab;

    // IDF: log(N / df) where df = number of docs containing the term
    const N = documents.length;
    const df = new Float64Array(vocab.size);
    for (const doc of documents) {
        const seen = new Set(doc);
        for (const term of seen) {
            const i = vocab.get(term);
            if (i !== undefined) df[i] += 1;
        }
    }

    corpus.idf = new Float64Array(vocab.size);
    for (let i = 0; i < vocab.size; i++) {
        // Smoothed IDF to avoid division by zero and extreme values
        corpus.idf[i] = Math.log((N + 1) / (df[i] + 1)) + 1;
    }
}

/**
 * Build a TF-IDF vector for a tokenized document.
 * @param {string[]} tokens
 * @returns {Float64Array}
 */
function buildVector(tokens) {
    const vec = new Float64Array(corpus.vocabulary.size);
    if (tokens.length === 0) return vec;

    // Term frequency (normalized by doc length)
    for (const t of tokens) {
        const idx = corpus.vocabulary.get(t);
        if (idx !== undefined) {
            vec[idx] += 1.0 / tokens.length;
        }
    }

    // Multiply by IDF
    for (let i = 0; i < vec.length; i++) {
        vec[i] *= corpus.idf[i];
    }

    return vec;
}

/**
 * Build a TF-IDF vector from weighted tokens (synonym-expanded).
 * @param {{term: string, weight: number}[]} weightedTokens
 * @returns {Float64Array}
 */
function buildWeightedVector(weightedTokens) {
    const vec = new Float64Array(corpus.vocabulary.size);
    if (weightedTokens.length === 0) return vec;

    let totalWeight = 0;
    for (const { term, weight } of weightedTokens) {
        const idx = corpus.vocabulary.get(term);
        if (idx !== undefined) {
            vec[idx] += weight;
            totalWeight += weight;
        }
    }

    // Normalize by total weight (like TF normalization)
    if (totalWeight > 0) {
        for (let i = 0; i < vec.length; i++) {
            vec[i] /= totalWeight;
        }
    }

    // Multiply by IDF
    for (let i = 0; i < vec.length; i++) {
        vec[i] *= corpus.idf[i];
    }

    return vec;
}

/**
 * Cosine similarity between two vectors.
 * @param {Float64Array} a
 * @param {Float64Array} b
 * @returns {number} 0–1
 */
function cosineSimilarity(a, b) {
    let dot = 0;
    let magA = 0;
    let magB = 0;
    for (let i = 0; i < a.length; i++) {
        dot += a[i] * b[i];
        magA += a[i] * a[i];
        magB += b[i] * b[i];
    }
    const denom = Math.sqrt(magA) * Math.sqrt(magB);
    return denom === 0 ? 0 : dot / denom;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Initialize the embedding corpus from the action registry.
 * Call this once; subsequent calls are no-ops.
 *
 * @param {Object[]} actions - All registered actions
 */
export function initEmbeddings(actions) {
    if (corpus.initialized) return;

    // Tokenize all action documents
    const documents = [];
    const actionIds = [];
    for (const action of actions) {
        const text = actionToText(action);
        const tokens = tokenize(text);
        documents.push(tokens);
        actionIds.push(action.id);
    }

    // Build vocabulary and IDF
    buildVocabulary(documents);

    // Pre-compute action vectors
    for (let i = 0; i < documents.length; i++) {
        const vec = buildVector(documents[i]);
        corpus.actionVectors.set(actionIds[i], vec);
    }

    corpus.initialized = true;
}

/**
 * Score an action's semantic relevance to a user message.
 *
 * @param {string} userMessage - Natural language query
 * @param {Object} action - Action definition
 * @returns {number} Similarity score (0–1)
 */
export function semanticScore(userMessage, action) {
    if (!corpus.initialized) return 0;

    const tokens = tokenize(userMessage);
    const expanded = expandWithSynonyms(tokens);
    const queryVec = buildWeightedVector(expanded);
    const actionVec = corpus.actionVectors.get(action.id);

    if (!actionVec) return 0;
    return cosineSimilarity(queryVec, actionVec);
}

/**
 * Rank all actions by semantic similarity to a user message.
 * Returns actions sorted by descending similarity score.
 *
 * @param {string} userMessage
 * @param {Object[]} actions
 * @returns {{action: Object, score: number}[]}
 */
export function rankActions(userMessage, actions) {
    const tokens = tokenize(userMessage);
    const expanded = expandWithSynonyms(tokens);
    const queryVec = buildWeightedVector(expanded);

    return actions
        .map((action) => {
            const actionVec = corpus.actionVectors.get(action.id);
            return {
                action,
                score: actionVec ? cosineSimilarity(queryVec, actionVec) : 0,
            };
        })
        .sort((a, b) => b.score - a.score);
}

/**
 * Check if embeddings have been initialized.
 * @returns {boolean}
 */
export function isInitialized() {
    return corpus.initialized;
}

/**
 * Reset state (for testing).
 */
export function resetEmbeddings() {
    corpus.vocabulary = new Map();
    corpus.idf = null;
    corpus.actionVectors = new Map();
    corpus.initialized = false;
}

// Export internals for testing
export {
    tokenize,
    stem,
    expandWithSynonyms,
    cosineSimilarity,
};
